import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import {
  Panel, PageHeader, StatTile, StatGrid, Chip, DataTable, TRow, StatusPill,
  BtnGhost, EmptyRow, Loading, CardHead,
} from "@/components/admin/kit";

/**
 * Operator console for the continuous sportsbook engine — built on the shared admin kit
 * so it matches the rest of /admin (Events, Users, …).
 */
const MODES = ["manual", "shadow", "assist", "primary"];
const MODE_HELP = {
  manual: "Engine dormant — legacy admin-import board only.",
  shadow: "Engine discovers + prices in memory; publishes nothing.",
  assist: "Engine auto-creates offers as DRAFT for preview.",
  primary: "Engine auto-publishes eligible markets live.",
};

// Display names for sportsbooks (clean labels, no raw slugs).
const OFFERS_GRID = "minmax(150px,2fr) minmax(74px,1fr) minmax(80px,1fr) 56px 64px minmax(120px,1.4fr) 48px";

const dash = (v) => (v == null || v === "" ? "—" : v);

export default function AdminSportsbookEngine() {
  const { user } = useAuth();
  // Engine-mode changes (/admin/sportsbook/config) are gated on super_admin ONLY
  // by the backend (require_super_admin rejects "owner"). Mirror that here so a
  // non-super admin never sees an enabled mode chip that would 403 on click.
  const isSuper = user?.role === "super_admin";
  const [health, setHealth] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [h, o] = await Promise.all([
        api.get("/admin/sportsbook/health"),
        api.get("/admin/sportsbook/offers", { params: { limit: 100 } }),
      ]);
      setHealth(h.data);
      setOffers(o.data || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load engine status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const setMode = async (mode) => {
    if (!isSuper) return;
    setSaving(true);
    try {
      await api.post("/admin/sportsbook/config", { sportsbook: { mode } });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to set mode");
    } finally {
      setSaving(false);
    }
  };


  if (loading) return <Loading>Loading engine status…</Loading>;

  const rt = health?.runtime || {};
  const stats = rt.last_stats || {};
  // engine-health returns provider_reachable (SharpAPI direct); older builds read gateway_reachable.
  // Distinguish three states: reachable (true), down (false), unknown (null/undefined — health
  // never reported a reachability flag). Never render a false "down" for the unknown case.
  const rawReach = health?.provider_reachable ?? health?.gateway_reachable;
  const providerState = rawReach == null ? "unknown" : rawReach ? "reachable" : "down";
  const providerColor = providerState === "reachable" ? "#00D632" : providerState === "down" ? "#EF4444" : "#a1a1aa";
  const amer = (v) => (v == null ? "—" : v > 0 ? `+${Math.round(v)}` : Math.round(v));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Sportsbook Engine"
        meta={`mode ${health?.mode || "—"} · source SharpAPI`}
        actions={<BtnGhost onClick={load}><Icon name="refresh" size={14} /> Refresh</BtnGhost>}
      />

      {err && (
        <Panel className="flex items-center gap-2 bg-[rgba(239,68,68,.08)] px-4 py-3 text-[13px] text-[#EF4444]" style={{ borderColor: "rgba(239,68,68,.35)" }}>
          {String(err)}
        </Panel>
      )}

      {/* Engine mode */}
      <Panel className="p-4">
        <CardHead title="Engine mode" right={saving && <span className="text-[12px] text-[#71717a]">saving…</span>} />
        <div className="mt-3 flex flex-wrap gap-1.5">
          {MODES.map((m) => (
            <Chip key={m} active={health?.mode === m} disabled={!isSuper || saving} onClick={() => setMode(m)}>{m}</Chip>
          ))}
        </div>
        <div className="mt-2 text-[12px] text-[#71717a]">{MODE_HELP[health?.mode] || ""}</div>
        {!isSuper && <div className="mt-1 text-[12px] text-[#a1a1aa]">Read-only view. Changing the engine mode requires the <span className="font-semibold text-[#d4d4d8]">super admin</span> role.</div>}
      </Panel>

      {/* Health tiles */}
      <StatGrid cols={6}>
        <StatTile icon="sportsbook" label="MODE" value={dash(health?.mode)} />
        <StatTile icon="pnl" label="PROVIDER" value={providerState} valueColor={providerColor} iconColor={providerColor} />
        <StatTile icon="refresh" label="ODDS STREAM" value={rt.stream_connected ? "connected" : "off"} valueColor={rt.stream_connected ? "#00D632" : "#a1a1aa"} />
        <StatTile icon="verified" label="RISK ENFORCED" value={health?.risk_enforced ? "yes" : "no"} valueColor={health?.risk_enforced ? "#00D632" : "#EF4444"} />
        <StatTile icon="mybets" label="OFFERS · OPEN" value={`${health?.offers?.total ?? 0} · ${health?.offers?.open ?? 0}`} />
        <StatTile icon="grade" label="SUSPENDED" value={health?.offers?.suspended ?? 0} valueColor={(health?.offers?.suspended ?? 0) > 0 ? "#EF4444" : "#fff"} />
      </StatGrid>

      {/* Skip reasons */}
      {rt.last_skipped && Object.keys(rt.last_skipped).length > 0 && (
        <Panel className="p-4">
          <CardHead title="Last-run skip reasons" />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(rt.last_skipped).map(([k, v]) => (
              <span key={k} className="rounded-md bg-[#18181C] px-2 py-1 text-[11px] text-[#a1a1aa]" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>{k}: {v}</span>
            ))}
          </div>
        </Panel>
      )}

      {/* Engine offers */}
      <div>
        <div className="mb-2"><CardHead title="Engine offers" count={offers.length} /></div>
        <DataTable cols={OFFERS_GRID} header={["EVENT", "MARKET", "SEL", "LINE", "PRICE", "STATUS", "VER"]}>
          {offers.length === 0 ? (
            <EmptyRow>No engine offers — engine dormant or shadow mode.</EmptyRow>
          ) : (
            offers.map((o) => (
              <TRow key={o.offer_id || o._id} cols={OFFERS_GRID}>
                <span className="truncate text-[#E4E4E7]">{o.home_team ? `${o.away_team} @ ${o.home_team}` : o.event_id}</span>
                <span className="text-[#a1a1aa]">{o.market}</span>
                <span className="text-[#a1a1aa]">{o.side}{o.player ? ` · ${o.player}` : ""}</span>
                <span className="tabular-nums">{o.line ?? "—"}</span>
                <span className="font-display font-bold tabular-nums">{amer(o.current?.american_odds)}</span>
                <StatusPill
                  status={o.status}
                  bg={o.status === "open" ? "rgba(0,214,50,.12)" : o.status === "suspended" ? "#26262B" : (o.status === "expired" || o.status === "void") ? "rgba(239,68,68,.14)" : "#18181C"}
                  fg={o.status === "open" ? "#00D632" : o.status === "suspended" ? "#d4d4d8" : (o.status === "expired" || o.status === "void") ? "#EF4444" : "#a1a1aa"}
                />
                <span className="tabular-nums text-[#71717a]">v{o.version}</span>
              </TRow>
            ))
          )}
        </DataTable>
      </div>
    </div>
  );
}
